import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Box,
  Flex,
  Text,
  Collapse,
  Link,
  Button,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  setLocationProp,
  removeLocation,
} from '../../../store/redux/slices/template.slice';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import type { LocationInput } from '../../../store/types/template';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import CustomPinIcon from '../../../theme/icons/CustomPinIcon';
import { CustomNavArrowIcon } from '../../../theme/icons/CustomNavArrowIcon';
import { APPLE_LOCATION_MESSAGE_MAX_CHARS } from './utils/locationUtils';
import LocationRadiusField from './LocationRadiusField';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

export type LocationWrapperProps = {
  location: LocationInput & { _id: string; _newlyAdded?: boolean };
  locationId: string;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRequireOpen: (id: string) => void;
  newlyAdded?: boolean;
  appleNotificationsOnly?: boolean;
  mutedLocationHeader?: boolean;
  googleWalletNoticeOnly?: boolean;
  onNavigateToAppleNotification?: (locationId: string) => void;
};

const LocationWrapper = ({
  location,
  locationId,
  index,
  isExpanded,
  onToggle,
  onRequireOpen,
  newlyAdded = false,
  appleNotificationsOnly = false,
  mutedLocationHeader = false,
  googleWalletNoticeOnly = false,
  onNavigateToAppleNotification,
}: LocationWrapperProps) => {
  const dispatch = useAppDispatch();
  const templateType = useAppSelector((s) => s.template.templateType);

  const locations = useAppSelector((s) => s.template.locations);
  const { validateField, validateSection, handleFocus, handleBlur } =
    useTemplateFieldValidation('locations');
  const errors = useAppSelector((s) => s.templateErrors);

  const locationNameRef = useRef<HTMLDivElement>(null);
  const locationAddressRef = useRef<HTMLDivElement>(null);
  const locationRadiusRef = useRef<HTMLDivElement>(null);
  const locationMessageRef = useRef<HTMLDivElement>(null);

  const fieldRefMap = appleNotificationsOnly
    ? { message: locationMessageRef }
    : googleWalletNoticeOnly
      ? {}
      : {
          name: locationNameRef,
          'latitude||longitude': locationAddressRef,
          radius: locationRadiusRef,
          message: locationMessageRef,
        };

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: 'locations',
    index,
    fieldRefMap,
  });

  const handleChange = useCallback(
    (field: keyof LocationInput, value: string | number | boolean) => {
      dispatch(setLocationProp({ index, field, value }));
      const nextLocations = locations.slice();
      nextLocations[index] = { ...nextLocations[index], [field]: value };
      validateField(`${index}.${field}`, value, { locations: nextLocations });
    },
    [dispatch, index, locations, validateField]
  );

  const handleRemoveLocation = useCallback(() => {
    const nextLocations = locations.filter((_, i) => i !== index);
    dispatch(removeLocation(index));
    void validateSection(nextLocations);
  }, [dispatch, index, locations, validateSection]);

  useEffect(() => {
    if (!newlyAdded) return;
    dispatch(
      setLocationProp({
        index,
        field: '_newlyAdded' as keyof LocationInput,
        value: false,
      })
    );
  }, [newlyAdded, dispatch, index]);

  const locationHasErrors = useMemo(() => {
    const errorKeys = Object.keys(errors);
    if (googleWalletNoticeOnly) return false;
    if (appleNotificationsOnly) {
      return errorKeys.some((key) => key === `locations.${index}.message`);
    }
    return errorKeys.some((key) => key.startsWith(`locations.${index}.`));
  }, [appleNotificationsOnly, errors, googleWalletNoticeOnly, index]);

  useEffect(() => {
    if (locationHasErrors) {
      onRequireOpen(locationId);
    }
  }, [locationHasErrors, locationId, onRequireOpen]);

  const rowScrollId = `location-row-${locationId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const messageTrimmed = (location.message ?? '').trim();

  return (
    <Box
      id={rowScrollId}
      p={3}
      border="1px solid"
      borderColor="lightGrey"
      borderRadius="md"
    >
      <Flex
        alignItems="center"
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
      >
        <Flex alignItems="center" gap={2} minW={0}>
          <CustomPinIcon
            boxSize={8}
            flexShrink={0}
            sx={
              mutedLocationHeader
                ? { '& path': { fill: '#666666' } }
                : undefined
            }
          />
          <Flex direction="column" alignItems="flex-start" gap={0.5} minW={0}>
            <Text
              fontWeight="500"
              noOfLines={1}
              color={mutedLocationHeader ? '#666666' : undefined}
            >
              {location.name ? location.name : 'New location'}
            </Text>
            {location.address ? (
              <Text
                fontSize="12px"
                fontWeight="400"
                color="#666666"
                noOfLines={2}
                lineHeight="short"
              >
                {location.address}
              </Text>
            ) : null}
          </Flex>
        </Flex>
        {isExpanded ? (
          <ChevronUpIcon boxSize="20px" />
        ) : (
          <ChevronDownIcon boxSize="20px" />
        )}
      </Flex>
      <Collapse in={isExpanded}>
        <Flex direction="column" gap={4} mt={6}>
          {googleWalletNoticeOnly ? (
            <Box bg="#F0F0F0" borderRadius="md" px={3} py={2.5}>
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="14px"
                color="#4C4C4C"
              >
                Google Wallet uses a standard system notification message.
              </Text>
            </Box>
          ) : appleNotificationsOnly ? (
            <>
              <FieldEditIndicator
                fieldRef={locationMessageRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="75px"
              >
                <FormControl isInvalid={!!errors[`locations.${index}.message`]}>
                  <FormLabel variant="templateBuilderLabel" mb={3}>
                    Notification message
                  </FormLabel>
                  <Textarea
                    variant="outlined"
                    value={location.message ?? ''}
                    placeholder="e.g. “Welcome! Show this pass in store today.”"
                    rows={3}
                    maxLength={APPLE_LOCATION_MESSAGE_MAX_CHARS}
                    resize="vertical"
                    onChange={(e) => {
                      const next = e.target.value.slice(
                        0,
                        APPLE_LOCATION_MESSAGE_MAX_CHARS
                      );
                      handleChange('message', next);
                    }}
                    onFocus={() => handleFocus(`locations.${index}.message`)}
                    onBlur={handleBlur}
                  />
                  <Text fontSize="12px" color="#666666" mt={1} pl={0.5}>
                    ({(location.message ?? '').length}/
                    {APPLE_LOCATION_MESSAGE_MAX_CHARS} characters)
                  </Text>
                </FormControl>
              </FieldEditIndicator>
              <Flex alignItems="center" justify="flex-end">
                <Link
                  variant="orange"
                  fontSize="14px"
                  aria-disabled={!messageTrimmed}
                  opacity={messageTrimmed ? 1 : 0.45}
                  cursor={messageTrimmed ? 'pointer' : 'not-allowed'}
                  onClick={() => {
                    if (!messageTrimmed) return;
                    handleChange('message', '');
                  }}
                >
                  Remove message
                </Link>
              </Flex>
            </>
          ) : (
            <>
              <FieldEditIndicator
                fieldRef={locationNameRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="60px"
              >
                <FormControl
                  isInvalid={!!errors[`locations.${index}.name`]}
                  isRequired
                  onPointerDownCapture={() =>
                    handleFocus(`locations.${index}.name`)
                  }
                >
                  <FormLabel variant="templateBuilderLabel" mb={3}>
                    Location Name
                  </FormLabel>
                  <Input
                    value={location.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Example: 'Coffee Shop – Victoria Station'"
                    onFocus={() => handleFocus(`locations.${index}.name`)}
                    onBlur={handleBlur}
                  />
                  <FormErrorMessage
                    pl={2}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                  >
                    <CustomErrorIcon />
                    {errors[`locations.${index}.name`]}
                  </FormErrorMessage>
                </FormControl>
              </FieldEditIndicator>
              <FieldEditIndicator
                fieldRef={locationRadiusRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="48px"
              >
                <LocationRadiusField
                  templateType={templateType}
                  radius={location.radius}
                  radiusError={errors[`locations.${index}.radius`]}
                  onRadiusChange={(meters) => handleChange('radius', meters)}
                  onRadiusFocus={() => handleFocus(`locations.${index}.radius`)}
                  onBlur={handleBlur}
                />
              </FieldEditIndicator>
              <Flex direction="column" gap={0}>
                <FieldEditIndicator
                  fieldRef={locationMessageRef}
                  isFieldBeingEdited={isFieldBeingEdited}
                  activeFieldRef={activeFieldRef}
                  left="-55px"
                  top="75px"
                >
                  <FormControl
                    isInvalid={!!errors[`locations.${index}.message`]}
                  >
                    <FormLabel variant="templateBuilderLabel" mt={3} mb={3}>
                      Apple notification
                    </FormLabel>
                    <Textarea
                      variant="outlined"
                      value={location.message ?? ''}
                      placeholder="e.g. “Welcome! Show this pass in store today.”"
                      rows={2}
                      maxLength={APPLE_LOCATION_MESSAGE_MAX_CHARS}
                      resize="vertical"
                      onChange={(e) => {
                        const next = e.target.value.slice(
                          0,
                          APPLE_LOCATION_MESSAGE_MAX_CHARS
                        );
                        handleChange('message', next);
                      }}
                      onFocus={() => handleFocus(`locations.${index}.message`)}
                      onBlur={handleBlur}
                    />
                    <Text fontSize="12px" color="#666666" mt={1} pl={1}>
                      ({(location.message ?? '').length}/
                      {APPLE_LOCATION_MESSAGE_MAX_CHARS} characters)
                    </Text>
                    <Flex mt={2} ml={2} alignItems="center" gap={2}>
                      <Text fontSize="14px" color="#4C4C4C">
                        Google wallet uses a standard system notification
                        message.
                      </Text>
                    </Flex>
                  </FormControl>
                </FieldEditIndicator>
              </Flex>
              {/* <FieldEditIndicator
                fieldRef={locationMessageRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="60px"
              >
                <FormLabel variant="templateBuilderLabel" my={3}>
                  Notification
                </FormLabel>
                <Button
                  type="button"
                  borderWidth="2px"
                  borderStyle="solid"
                  borderColor="appleBlue"
                  bg="white"
                  color="appleBlue"
                  width="100%"
                  alignItems="center"
                  gap={2}
                  transition="background-color 0.2s ease"
                  _hover={{
                    bg: 'white',
                    color: 'appleBlue',
                    opacity: 0.8,
                  }}
                  onClick={() => onNavigateToAppleNotification?.(locationId)}
                >
                  <Text textStyle="bodyFamilyRegular" fontSize="14px">
                    Add Apple notification
                  </Text>
                  <CustomNavArrowIcon boxSize="12px" fill="currentColor" />
                </Button>
              </FieldEditIndicator> */}
              <Flex mt={2} alignItems="center" justify="flex-end" gap={2}>
                <Link
                  color="dangerRed"
                  fontSize="14px"
                  onClick={handleRemoveLocation}
                >
                  Remove location
                </Link>
              </Flex>
            </>
          )}
        </Flex>
      </Collapse>
    </Box>
  );
};

export default LocationWrapper;
